/**
 * DashboardPage — visão agregada de Links (status, velocidade média, qualidade/dispositivos) +
 * histórico do período, com filtro por Template.
 *
 * Estrutura espelhada em hay-hotspot/assets/reactweb/src/pages/Auth/Dashboard/DashboardTemplate.js
 * (Splitter: cards/gráficos à esquerda, histórico à direita; `surface-card` como fundo — nunca
 * uma cor fixa, pra acompanhar o tema claro/escuro em vez de contrastar com o `#343A40` do layout,
 * como acontecia na versão anterior desta página, sem nenhum wrapper de card). Sem o socket de
 * tempo real do hotspot (`UsuarioLog` tem eventos ao vivo via WebSocket; o fluxo de teste do
 * LinkInsight não tem infraestrutura equivalente ainda) — os dados só são recarregados ao mudar
 * filtro ou reabrir a tela.
 */
import React, { Component } from 'react';
import { Divider } from 'primereact/divider';
import { Splitter, SplitterPanel } from 'primereact/splitter';
import { ApiService } from 'hay-core-react';
import { UtilService } from 'hay-core-react';
import DashboardFiltros from './DashboardFiltros';
import DashboardStatCard from './DashboardStatCard';
import DashboardGraficoPizza from './DashboardGraficoPizza';
import DashboardTimeline from './DashboardTimeline';
import DashboardPeriodoEnum from '../../../enums/DashboardPeriodoEnum';

const PERIODO_CUSTOM = 'main.dashboard.periodo.periodo';

function formatarMedia(valor, sufixo, casas = 1) {
  if (valor === null || valor === undefined) return '—';
  return `${valor.toFixed(casas)} ${sufixo}`;
}

class DashboardPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      periodo: DashboardPeriodoEnum.getDefault().id,
      data: Date.now(),
      dataFim: null,
      template: null,
      dados: null,
      carregando: true,
      buscando: false,
    };
  }

  componentDidMount() {
    this.buscarDados();
  }

  async buscarDados() {
    this.setState({ buscando: true });
    const { periodo, data, dataFim, template } = this.state;
    const resultado = await ApiService.post('dashboard/report', {
      periodo,
      data,
      dataFim: periodo === PERIODO_CUSTOM ? dataFim : undefined,
      monitoramentotemplate: template?.id,
    });
    this.setState({
      dados: resultado?.status ? resultado.data : this.state.dados,
      carregando: false,
      buscando: false,
    });
  }

  onChangePeriodo = (periodo) => {
    this.setState({ periodo, data: Date.now(), dataFim: null }, () => this.buscarDados());
  }

  onChangeData = (data) => {
    this.setState({ data }, () => this.buscarDados());
  }

  onChangeRange = (data, dataFim) => {
    this.setState({ data, dataFim }, () => {
      if (data && dataFim) this.buscarDados();
    });
  }

  onChangeTemplate = (template) => {
    this.setState({ template }, () => this.buscarDados());
  }

  renderConteudo() {
    const { dados, buscando } = this.state;
    const cards = dados?.cards || {};
    const graficos = dados?.graficos || {};
    const velocidade = cards.velocidade || {};
    const ipv6 = cards.ipv6 || {};

    return (
      <div style={UtilService.reactEnabled(!buscando)}>
        <div className="mb-3">
          <DashboardFiltros
            periodo={this.state.periodo}
            data={this.state.data}
            dataFim={this.state.dataFim}
            template={this.state.template}
            onChangePeriodo={this.onChangePeriodo}
            onChangeData={this.onChangeData}
            onChangeRange={this.onChangeRange}
            onChangeTemplate={this.onChangeTemplate}
          />
        </div>

        <Splitter layout="horizontal" style={{ height: '75vh' }} className="border-round">
          <SplitterPanel size={50} minSize={25} className="overflow-y-auto">
            <div className="p-3">
              <div className="grid">
                <div className="col-12 md:col-6">
                  <DashboardStatCard
                    icone="pi-share-alt"
                    cor="blue"
                    titulo={UtilService.traduzir('main.dashboard.card.totallinks')}
                    valor={cards.totalLinks ?? 0}
                    help={UtilService.traduzir('main.dashboard.card.totallinks.help')}
                  />
                </div>
                <div className="col-12 md:col-6">
                  <DashboardStatCard
                    icone="pi-clock"
                    cor="orange"
                    titulo={UtilService.traduzir('main.dashboard.card.aguardando')}
                    valor={cards.aguardando ?? 0}
                    help={UtilService.traduzir('main.dashboard.card.aguardando.help')}
                  />
                </div>
                <div className="col-12 md:col-6">
                  <DashboardStatCard
                    icone="pi-spin pi-spinner"
                    cor="cyan"
                    titulo={UtilService.traduzir('main.dashboard.card.iniciados')}
                    valor={cards.iniciados ?? 0}
                    help={UtilService.traduzir('main.dashboard.card.iniciados.help')}
                  />
                </div>
                <div className="col-12 md:col-6">
                  <DashboardStatCard
                    icone="pi-check-circle"
                    cor="green"
                    titulo={UtilService.traduzir('main.dashboard.card.finalizados')}
                    valor={cards.finalizados ?? 0}
                    help={UtilService.traduzir('main.dashboard.card.finalizados.help')}
                  />
                </div>
              </div>

              <Divider align="left" type="solid">
                <b>{UtilService.traduzir('main.dashboard.secao.velocidade')}</b>
              </Divider>

              <div className="grid">
                <div className="col-12 md:col-4">
                  <DashboardStatCard
                    icone="pi-cloud-download"
                    cor="green"
                    titulo={UtilService.traduzir('main.dashboard.card.download')}
                    valor={formatarMedia(velocidade.download, 'Mb/s')}
                    help={UtilService.traduzir('main.dashboard.card.download.help')}
                  />
                </div>
                <div className="col-12 md:col-4">
                  <DashboardStatCard
                    icone="pi-cloud-upload"
                    cor="blue"
                    titulo={UtilService.traduzir('main.dashboard.card.upload')}
                    valor={formatarMedia(velocidade.upload, 'Mb/s')}
                    help={UtilService.traduzir('main.dashboard.card.upload.help')}
                  />
                </div>
                <div className="col-12 md:col-4">
                  <DashboardStatCard
                    icone="pi-wave-pulse"
                    cor="indigo"
                    titulo={UtilService.traduzir('main.dashboard.card.jitter')}
                    valor={formatarMedia(velocidade.jitter, 'ms')}
                    help={UtilService.traduzir('main.dashboard.card.jitter.help')}
                  />
                </div>
                <div className="col-12 md:col-4">
                  <DashboardStatCard
                    icone="pi-stopwatch"
                    cor="teal"
                    titulo={UtilService.traduzir('main.dashboard.card.ping')}
                    valor={formatarMedia(velocidade.ping, 'ms')}
                    help={UtilService.traduzir('main.dashboard.card.ping.help')}
                  />
                </div>
                <div className="col-12 md:col-4">
                  <DashboardStatCard
                    icone="pi-star"
                    cor="purple"
                    titulo={UtilService.traduzir('main.dashboard.card.classificacaomedia')}
                    valor={cards.classificacaoMedia !== null && cards.classificacaoMedia !== undefined ? `${cards.classificacaoMedia.toFixed(0)}%` : '—'}
                    help={UtilService.traduzir('main.dashboard.card.classificacaomedia.help')}
                  />
                </div>
                <div className="col-12 md:col-4">
                  <DashboardStatCard
                    icone="pi-sitemap"
                    cor="pink"
                    titulo={UtilService.traduzir('main.dashboard.card.ipv6')}
                    valor={ipv6.total ? `${Math.round((ipv6.comIpv6 / ipv6.total) * 100)}%` : '—'}
                    subtitulo={ipv6.total ? `${ipv6.comIpv6}/${ipv6.total}` : undefined}
                    help={UtilService.traduzir('main.dashboard.card.ipv6.help')}
                  />
                </div>
              </div>

              <Divider align="left" type="solid">
                <b>{UtilService.traduzir('main.dashboard.secao.qualidade')}</b>
              </Divider>

              <div className="grid">
                <div className="col-12 md:col-6" style={{ minWidth: 0 }}>
                  <DashboardGraficoPizza titulo={UtilService.traduzir('main.dashboard.grafico.streamqualidade')} itens={graficos.streamQualidade} help={UtilService.traduzir('main.dashboard.grafico.streamqualidade.help')} />
                </div>
                <div className="col-12 md:col-6" style={{ minWidth: 0 }}>
                  <DashboardGraficoPizza titulo={UtilService.traduzir('main.dashboard.grafico.sistemaoperacional')} itens={graficos.sistemaOperacional} help={UtilService.traduzir('main.dashboard.grafico.sistemaoperacional.help')} />
                </div>
                <div className="col-12 md:col-6" style={{ minWidth: 0 }}>
                  <DashboardGraficoPizza titulo={UtilService.traduzir('main.dashboard.grafico.navegador')} itens={graficos.navegador} help={UtilService.traduzir('main.dashboard.grafico.navegador.help')} />
                </div>
                <div className="col-12 md:col-6" style={{ minWidth: 0 }}>
                  <DashboardGraficoPizza titulo={UtilService.traduzir('main.dashboard.grafico.cpu')} itens={graficos.cpu} help={UtilService.traduzir('main.dashboard.grafico.cpu.help')} />
                </div>
              </div>
            </div>
          </SplitterPanel>

          <SplitterPanel size={50} minSize={25} className="overflow-y-auto">
            <div className="p-3">
              <Divider align="left" type="solid">
                <b>{UtilService.traduzir('main.dashboard.secao.timeline')}</b>
              </Divider>
              <DashboardTimeline itens={dados?.historico} />
            </div>
          </SplitterPanel>
        </Splitter>
      </div>
    );
  }

  render() {
    if (this.state.carregando) {
      return (
        <div className="surface-card border-round shadow-2 h-full flex justify-content-center align-items-center">
          <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }} />
        </div>
      );
    }

    return (
      <div className="surface-card border-round shadow-2 h-full p-3">
        {this.renderConteudo()}
      </div>
    );
  }
}

export default DashboardPage;
